import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { StreamChat, Channel, UserResponse } from 'stream-chat';
import { Observable, of, firstValueFrom, BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { RequestHelpersService } from '../shared/services/request-helpers.service';
import {
  MOCK_SUPPORT_MESSAGES,
  MOCK_SUPPORT_USER,
} from './mock-support-chat.config';

import { ChannelService } from 'stream-chat-angular';
import { StorageService } from '../shared/services/storage-service.service';
import { SessionStorageKeys } from '../constants/storageKeys';

export interface ChatUser {
  id: string;
  name: string;
  image: string;
}

export interface SupportMessageResponse {
  sent: boolean;
  redirect: boolean;
  redirectUrl: string;
}

@Injectable({
  providedIn: 'root',
})
export class ChatService {
  private chatUrl = `${environment.apiUrl}/stream-chat`;
  private useMock = environment.mockSupportChat ?? false; // Add this flag to environment.ts for easy switching
  public chatClient: StreamChat | null = null;
  public channel: Channel | null = null;

  // Mock mode additions
  public mockChannel: any = null;
  private mockMessages$ = new BehaviorSubject<any[]>(
    MOCK_SUPPORT_MESSAGES.slice()
  );

  constructor(
    private http: HttpClient,
    private requestHelpers: RequestHelpersService,
    private channelService: ChannelService,
    private sesssionStorage: StorageService
  ) {}

  sanitizeStreamUserId(userId) {
    // Only allow a-z, 0-9, @, _, and -
    return userId.toLowerCase().replace(/[^a-z0-9@_-]/g, '_');
  }

  sanitizeStreamChatId(id) {
    // Only allow a-z, 0-9, !, -, _
    return id.toLowerCase().replace(/[^a-z0-9!_-]/g, '_');
  }

  /**
   * Initialize the chat client and channel for support chat
   */
  async init(user: ChatUser): Promise<void> {
    if (this.useMock) {
      this.chatClient = null;
      this.channel = null;
      // Set up a mock channel object for the UI to bind to
      this.mockChannel = {
        id: 'mock-support-channel',
        name: 'Support',
        members: [
          MOCK_SUPPORT_USER,
          {
            id: 'support',
            name: 'Support',
            image: '/assets/images/pipa_headshot.png',
          },
        ],
      };
      // Reset mock messages
      this.mockMessages$.next(MOCK_SUPPORT_MESSAGES.slice());
      return;
    }
    // Sanitize user ID to match Stream's requirements
    const sanitizedUserId = this.sanitizeStreamUserId(user.id);
    const userObject: ChatUser = {
      id: sanitizedUserId,
      name: user.name,
      image: user.image,
    };
    // Only connect if not already connected as this user
    if (!this.chatClient || this.chatClient.userID !== sanitizedUserId) {
      // Disconnect previous user if already connected
      if (this.chatClient) {
        try {
          await this.chatClient.disconnectUser();
        } catch (e) {
          console.warn(
            'StreamChat disconnectUser error (safe to ignore if not connected):',
            e
          );
        }
      }
      const token = await firstValueFrom(this.getToken(user));
      this.sesssionStorage.saveToSession(
        SessionStorageKeys.CHAT_SESSION,
        token
      );
      this.chatClient = StreamChat.getInstance(environment.streamChatKey);
      await this.chatClient.connectUser(userObject, token);
    }
    // 3. Create or get support channel
    const channelId = `support-${sanitizedUserId}`;
    let channel: Channel | null = null;
    try {
      channel = await firstValueFrom(
        this.createSupportChannel({
          id: channelId,
          name: user.name,
          image: user.image,
        })
      );
      if (!channel) console.warn('No channel found');
      await channel.watch();
      this.channel = channel;
    } catch (err) {
      this.channel = null;
      console.error('Error initializing support chat channel:', err);
    }
  }

  /**
   * Get Stream Chat token for user
   */
  getToken(user: ChatUser): Observable<string> {
    if (this.useMock) {
      return of('mock-token');
    }
    return this.http
      .post<{ token: string }>(
        `${this.chatUrl}/token`,
        { user },
        {
          headers: this.requestHelpers.createHeaders(),
        }
      )
      .pipe(map((res) => res.token));
  }

  /**
   * Create or get a support channel for the user
   */
  createSupportChannel(user: ChatUser): Observable<Channel> {
    if (this.useMock) {
      return of({ id: 'mock-support-channel' } as any);
    }
    return this.http
      .post<{ channelId: string }>(
        `${this.chatUrl}/support-channel`,
        {
          userId: user.id,
        },
        {
          headers: this.requestHelpers.createHeaders(),
        }
      )
      .pipe(
        map((res) => {
          if (!this.chatClient) {
            console.error('Chat client not initialized');
            return null as any;
          }
          return this.chatClient.channel('messaging', res.channelId);
        })
      );
  }

  /**
   * Send a support message
   */
  sendSupportMessage(message: string): Observable<any> {
    if (this.useMock) {
      // Add the new message to the mock messages array
      const newMsg = {
        id: Date.now(),
        user: MOCK_SUPPORT_USER.id,
        text: message,
        createdAt: new Date(),
      };
      const updated = [...this.mockMessages$.value, newMsg];
      this.mockMessages$.next(updated);
      return of({ success: true, message: newMsg });
    }
    if (!this.channel) console.error('Chat client not initialized');
    return this.http.post<SupportMessageResponse>(
      `${this.chatUrl}/support-message`,
      {
        // channelId: this.channel.id,
        message,
      },
      {
        headers: this.requestHelpers.createHeaders(),
      }
    );
  }

  /**
   * Observable for mock messages (for the component to subscribe to in mock mode)
   */
  getMockMessages() {
    return this.mockMessages$.asObservable();
  }

  /**
   * POST /disconnect
   * Disconnect the chat client
   */
  disconnect(): Observable<any> {
    if (this.useMock) {
      return of({ success: true });
    }
    return this.http.post(
      `${this.chatUrl}/disconnect`,
      {},
      {
        headers: this.requestHelpers.createHeaders(),
      }
    );
  }
}
